import { Caption1, Menu, MenuItem, MenuList, MenuPopover, MenuTrigger, makeStyles, mergeClasses, shorthands, tokens } from "@fluentui/react-components";
import {
  Checkmark12Filled,
  CheckmarkCircle16Filled,
  ChevronDown16Regular,
  ChevronRight16Regular,
  Circle16Regular,
  MoreHorizontal16Regular,
} from "@fluentui/react-icons";
import { Fragment, useState } from "react";
import { AppDialog, ConfirmDialog } from "@/components/app-dialog";
import { DateField } from "@/components/date-field";
import { FuiButton, FuiDropdown, FuiInput } from "@/components/fui-control";
import { GhostEmptyState } from "@/components/ghost-empty-state";
import { useDialogLayer } from "@/components/ui/dialog-layer";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { resolveCalendar, HOLIDAY_CATALOGS } from "@/engine/holiday-catalogs";
import { SegmentedControl } from "@/features/resources/SegmentedControl";
import type { ScheduleImpact } from "@/engine/diff";
import type { InsertStart, ProgressMethod } from "@/engine/types";
import { OPTIONAL_COLUMNS, setHiddenColumns } from "@/features/wbs-grid/WbsGrid";
import { formatDate } from "@/lib/date-format";
import { fmtDelta } from "@/lib/format";
import { DEFAULT_HIDDEN_COLUMNS, useProjectStore, type BarLabel, type Density, type PinBoundary } from "@/store/project-store";
import { normalizeDefaultDuration, resolveInsertStart } from "@/store/tree-ops";
import { useSettingsHub } from "@/shell/settings-hub";
import { UI_TERMS } from "@/features/access/terms";
import { usePlanSurface } from "@/features/access/use-plan-access";
import { isDesktop } from "@/desktop/runtime";
import { connectionText, useConnectionStatus } from "@/desktop/use-connection-status";
import type { CalendarDefinition } from "@/engine/calendar";
import {
  WEEK_ORDER,
  WEEKDAY_LABELS,
  WEEKDAY_LONG_LABELS,
  WORKWEEK_PRESETS,
  addPlanHoliday,
  clearHolidayYear,
  describeWorkweek,
  fmtHolidayYear,
  groupHolidaysByYear,
  holidayDateConflict,
  matchWorkweekPreset,
  removePlanHoliday,
  setHolidayRowEnabled,
  toggleHolidayCatalog,
  toggleWorkday,
  updatePlanHoliday,
  yearsBetween,
  type HolidayRow,
} from "./calendar-settings-model";

const PROGRESS_METHODS: Array<{ v: ProgressMethod; label: string; hint: string }> = [
  { v: "percent", label: "직접 입력 (%)", hint: "입력한 %를 그대로 씁니다" },
  { v: "0/100", label: "끝나면 100%", hint: "끝나기 전엔 0%, 끝나면 100%" },
  { v: "50/50", label: "시작 50% · 완료 100%", hint: "시작하면 50%, 끝나면 100%" },
  { v: "milestone", label: "마일스톤으로 측정", hint: "하위 마일스톤을 지날 때마다 올라갑니다" },
];

const INSERT_START_OPTIONS: Array<{ value: InsertStart; label: string; hint: string }> = [
  { value: "anchor", label: "기준 작업과 같은 날", hint: "선택한 작업(하위 추가 시 상위)의 시작일. 앞 작업을 연결하면 그 작업을 따릅니다" },
  { value: "link", label: "기준 작업 뒤에 연결", hint: "선택한 작업 뒤에 FS로 연결합니다. 하위 추가 시에는 마지막 하위 작업 뒤" },
  { value: "project", label: "플랜 시작일", hint: "연결하기 전까지 플랜 시작일에 놓입니다" },
];

const BAR_LABEL_OPTIONS = [
  { value: "name", label: "작업 이름" },
  { value: "dates", label: "시작 – 종료" },
  { value: "assignee", label: "담당자" },
  { value: "none", label: "없음" },
];

const DENSITY_OPTIONS = [
  { value: "compact", label: "컴팩트" },
  { value: "default", label: "기본" },
  { value: "comfortable", label: "여유" },
];

const useStyles = makeStyles({
  panel: {
    flex: 1,
    minHeight: 0,
    overflow: "auto",
    minWidth: 0,
  },
  group: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    marginBottom: "20px",
    ":last-child": { marginBottom: 0 },
  },
  groupTitle: {
    marginBottom: "4px",
    color: tokens.colorNeutralForeground3,
  },
  groupBody: {
    display: "flex",
    flexDirection: "column",
  },
  row: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "24px",
    minHeight: "52px",
    padding: "12px 0",
    borderBottomWidth: "1px",
    borderBottomStyle: "solid",
    borderBottomColor: tokens.colorNeutralStroke2,
  },
  themeChoices: {
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
    flexShrink: 0,
  },
  themeBtn: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "6px",
    width: "76px",
    padding: 0,
    border: "none",
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground3,
    fontSize: "12px",
    lineHeight: "16px",
    fontWeight: tokens.fontWeightRegular,
    cursor: "pointer",
    ":hover": { color: tokens.colorNeutralForeground1 },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "2px",
      borderRadius: tokens.borderRadiusMedium,
    },
  },
  themeBtnOn: {
    color: tokens.colorNeutralForeground1,
  },
  preview: {
    display: "flex",
    width: "76px",
    height: "52px",
    boxSizing: "border-box",
    overflow: "hidden",
    borderRadius: tokens.borderRadiusLarge,
    borderTopWidth: "1px",
    borderRightWidth: "1px",
    borderBottomWidth: "1px",
    borderLeftWidth: "1px",
    borderTopStyle: "solid",
    borderRightStyle: "solid",
    borderBottomStyle: "solid",
    borderLeftStyle: "solid",
    borderTopColor: tokens.colorNeutralStroke2,
    borderRightColor: tokens.colorNeutralStroke2,
    borderBottomColor: tokens.colorNeutralStroke2,
    borderLeftColor: tokens.colorNeutralStroke2,
  },
  previewOn: {
    borderTopWidth: "2px",
    borderRightWidth: "2px",
    borderBottomWidth: "2px",
    borderLeftWidth: "2px",
    borderTopColor: tokens.colorNeutralForeground1,
    borderRightColor: tokens.colorNeutralForeground1,
    borderBottomColor: tokens.colorNeutralForeground1,
    borderLeftColor: tokens.colorNeutralForeground1,
  },
  previewLight: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    minWidth: 0,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  previewDark: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    minWidth: 0,
    backgroundColor: tokens.colorNeutralBackgroundInverted,
  },
  chrome: {
    height: "8px",
    flexShrink: 0,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  chromeDark: {
    backgroundColor: tokens.colorNeutralBackground4,
  },
  previewBody: {
    display: "flex",
    flex: 1,
    minHeight: 0,
    gap: "4px",
    padding: "5px",
  },
  rail: {
    width: "10px",
    flexShrink: 0,
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  railDark: {
    backgroundColor: tokens.colorNeutralBackground4,
  },
  lines: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    justifyContent: "center",
    gap: "3px",
    minWidth: 0,
  },
  previewLine: {
    height: "3px",
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorNeutralStroke1,
  },
  previewLineShort: {
    width: "62%",
  },
  previewLineDark: {
    backgroundColor: tokens.colorNeutralBackground3,
  },
  appearanceContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    minWidth: 0,
    width: "100%",
  },
  appearanceCard: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    padding: "20px",
    borderRadius: tokens.borderRadiusLarge,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  appearanceCardHead: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  appearanceCardTitle: {
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground1,
  },
  appearanceCardSubtitle: {
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    color: tokens.colorNeutralForeground3,
  },
  themeCardsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: "12px",
    minWidth: 0,
  },
  themeChoiceCard: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    padding: "14px",
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    cursor: "pointer",
    textAlign: "left",
    transitionProperty: "border-color, box-shadow, background-color",
    transitionDuration: tokens.durationFaster,
    ":hover": {
      border: `1px solid ${tokens.colorNeutralStroke1}`,
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
  themeChoiceCardOn: {
    border: `1px solid ${tokens.colorBrandBackground}`,
    boxShadow: `0 0 0 1px ${tokens.colorBrandBackground}`,
    backgroundColor: tokens.colorNeutralBackground1Selected,
    ":hover": {
      border: `1px solid ${tokens.colorBrandBackground}`,
      backgroundColor: tokens.colorNeutralBackground1Selected,
    },
  },
  themeChoicePreview: {
    height: "72px",
    width: "100%",
    borderRadius: "6px",
    overflow: "hidden",
    display: "flex",
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  themeChoiceMeta: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  themeChoiceHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
  },
  themeChoiceTitle: {
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground1,
  },
  themeChoiceDesc: {
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    color: tokens.colorNeutralForeground3,
  },
  themeRadioIcon: {
    color: tokens.colorBrandForeground1,
    flexShrink: 0,
  },
  themeRadioOff: {
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
  },
  densityCardsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: "12px",
    minWidth: 0,
  },
  densityCard: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    padding: "14px",
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    cursor: "pointer",
    textAlign: "left",
    transitionProperty: "border-color, box-shadow, background-color",
    transitionDuration: tokens.durationFaster,
    ":hover": {
      border: `1px solid ${tokens.colorNeutralStroke1}`,
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
  densityCardOn: {
    border: `1px solid ${tokens.colorBrandBackground}`,
    boxShadow: `0 0 0 1px ${tokens.colorBrandBackground}`,
    backgroundColor: tokens.colorNeutralBackground1Selected,
    ":hover": {
      border: `1px solid ${tokens.colorBrandBackground}`,
      backgroundColor: tokens.colorNeutralBackground1Selected,
    },
  },
  densityLinesWrap: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    height: "56px",
    padding: "0 10px",
    borderRadius: "6px",
    backgroundColor: tokens.colorNeutralBackground3,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  densityLine: {
    height: "4px",
    borderRadius: "2px",
    backgroundColor: tokens.colorNeutralStroke1,
  },
  colorGuideGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: "12px",
  },
  colorGuideItem: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    padding: "12px 14px",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  colorGuideDot: {
    width: "12px",
    height: "12px",
    borderRadius: tokens.borderRadiusCircular,
    flexShrink: 0,
    marginTop: "3px",
  },
  colorGuideText: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    minWidth: 0,
  },
  colorGuideName: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground1,
  },
  colorGuideDesc: {
    fontSize: "11px",
    lineHeight: "15px",
    color: tokens.colorNeutralForeground3,
  },
  rowText: { minWidth: 0 },
  rowLabel: {
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground1,
  },
  hint: { color: tokens.colorNeutralForeground3 },
  checks: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    columnGap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalXS} 0`,
  },
  check: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalXS} 0`,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground1,
  },
  linkRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalS} 0`,
  },
  link: {
    border: "none",
    background: "transparent",
    padding: 0,
    cursor: "pointer",
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    fontWeight: tokens.fontWeightRegular,
    ":hover": { color: tokens.colorNeutralForeground1 },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
  viewContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    minWidth: 0,
    width: "100%",
  },
  viewCard: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    padding: "20px",
    borderRadius: tokens.borderRadiusLarge,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  viewCardHead: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  viewCardTitle: {
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground1,
  },
  viewCardSubtitle: {
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    color: tokens.colorNeutralForeground3,
  },
  viewSettingList: {
    display: "flex",
    flexDirection: "column",
  },
  viewItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    minHeight: "52px",
    padding: "12px 0",
    borderBottomWidth: "1px",
    borderBottomStyle: "solid",
    borderBottomColor: tokens.colorNeutralStroke2,
    ":last-child": {
      borderBottom: "none",
    },
  },
  viewItemText: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    minWidth: 0,
    flex: 1,
  },
  viewItemTitle: {
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground1,
  },
  viewItemHint: {
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    color: tokens.colorNeutralForeground3,
  },
  columnsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(136px, 1fr))",
    gap: "8px",
    paddingTop: "6px",
  },
  columnChip: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 12px",
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    cursor: "pointer",
    transitionProperty: "background-color, border-color",
    transitionDuration: tokens.durationFaster,
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  columnChipOn: {
    backgroundColor: tokens.colorNeutralBackground1Selected,
    border: `1px solid ${tokens.colorBrandStroke1}`,
  },
  columnActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "8px",
    paddingTop: "12px",
    borderTopWidth: "1px",
    borderTopStyle: "solid",
    borderTopColor: tokens.colorNeutralStroke2,
  },
  weekRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalS} 0`,
  },
  dayChips: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  dayGap: {
    width: "8px",
    flexShrink: 0,
  },
  dayChip: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "32px",
    height: "28px",
    padding: 0,
    border: "none",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    cursor: "pointer",
    transitionProperty: "background-color, color",
    transitionDuration: tokens.durationFaster,
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover, color: tokens.colorNeutralForeground1 },
    ":active": { backgroundColor: tokens.colorNeutralBackground1Pressed },
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: "1px" },
  },
  dayChipOn: {
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorNeutralForeground1,
    ":hover": { backgroundColor: tokens.colorBrandBackground2Hover, color: tokens.colorNeutralForeground1 },
    ":active": { backgroundColor: tokens.colorBrandBackground2Pressed },
  },
  presets: {
    display: "inline-flex",
    alignItems: "center",
    gap: "2px",
  },
  preset: {
    height: "28px",
    padding: "0 8px",
    border: "none",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    cursor: "pointer",
    whiteSpace: "nowrap",
    ":hover": { color: tokens.colorNeutralForeground1 },
    ":active": { backgroundColor: tokens.colorNeutralBackground1Pressed },
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: "1px" },
  },
  presetOn: {
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover, color: tokens.colorNeutralForeground1 },
  },
  error: {
    display: "block",
    paddingBottom: tokens.spacingVerticalS,
    color: tokens.colorPaletteRedForeground1,
  },
  impact: {
    position: "sticky",
    top: 0,
    zIndex: 1,
    marginBottom: tokens.spacingVerticalS,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusMedium,
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground2,
    fontVariantNumeric: "tabular-nums",
  },
  holidayAdd: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalS} 0`,
  },
  holidayDate: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: "12rem",
    minWidth: "9rem",
  },
  catalogList: {
    display: "flex",
    flexDirection: "column",
    paddingBottom: tokens.spacingVerticalXS,
  },
  catalogRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: tokens.spacingHorizontalS,
    padding: "4px 0",
    cursor: "pointer",
  },
  catalogCopy: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    gap: "1px",
  },
  holidayFilter: {
    padding: `${tokens.spacingVerticalXS} 0 ${tokens.spacingVerticalS}`,
  },
  holidayBody: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    gap: "1px",
  },
  holidayNote: {
    color: tokens.colorNeutralForeground3,
  },
  editStack: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    minWidth: 0,
  },
  editActions: {
    display: "flex",
    gap: tokens.spacingHorizontalXS,
  },
  filterEmpty: {
    display: "block",
    padding: `${tokens.spacingVerticalXS} 0 ${tokens.spacingVerticalS}`,
    color: tokens.colorNeutralForeground3,
  },
  year: {
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    padding: `${tokens.spacingVerticalXXS} 0`,
  },
  yearHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalS,
  },
  yearToggle: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    minWidth: 0,
    height: "28px",
    padding: "0 4px",
    marginLeft: "-4px",
    border: "none",
    borderRadius: tokens.borderRadiusSmall,
    background: "transparent",
    cursor: "pointer",
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    fontVariantNumeric: "tabular-nums",
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
    ":active": { backgroundColor: tokens.colorNeutralBackground1Pressed },
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: "1px" },
  },
  holidayList: {
    display: "grid",
    gridTemplateColumns: "max-content minmax(0, 1fr) 24px",
    columnGap: tokens.spacingHorizontalL,
    padding: `0 0 ${tokens.spacingVerticalXS}`,
    margin: 0,
    listStyle: "none",
  },
  holidayLabel: {
    fontVariantNumeric: "tabular-nums",
  },
  holidayName: {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: "inherit",
  },
  menuBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "24px",
    height: "24px",
    flexShrink: 0,
    padding: 0,
    border: "none",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground3,
    cursor: "pointer",
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover, color: tokens.colorNeutralForeground1 },
    ":active": { backgroundColor: tokens.colorNeutralBackground1Pressed },
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: "1px" },
  },
  popover: {
    zIndex: 100,
    pointerEvents: "auto",
  },
  holiday: {
    display: "grid",
    gridTemplateColumns: "subgrid",
    gridColumn: "1 / -1",
    alignItems: "center",
    minWidth: 0,
    minHeight: "32px",
    borderRadius: tokens.borderRadiusMedium,
    padding: "4px 0 4px 18px",
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    fontVariantNumeric: "tabular-nums",
    color: tokens.colorNeutralForeground1,
    borderBottomWidth: "1px",
    borderBottomStyle: "solid",
    borderBottomColor: tokens.colorNeutralStroke2,
    ":last-child": { borderBottomColor: "transparent" },
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
  },
  holidayMuted: { color: tokens.colorNeutralForeground3 },
});

export function SettingsDialog() {
  const styles = useStyles();
  const open = useProjectStore((s) => s.dialog === "settings");
  const close = () => useProjectStore.getState().openDialog(null);
  const canEditSchedule = usePlanSurface().editSchedule;
  return (
    <AppDialog open={open} onClose={close} title={UI_TERMS.viewSettings.ko} description={canEditSchedule ? "보기 옵션은 이 컴퓨터에만, 일정·캘린더 옵션은 플랜에 저장됩니다." : "보기 옵션은 이 컴퓨터에만 저장됩니다."} width="md" height={480}>
      <Tabs defaultValue="view" className="flex min-h-0 flex-1 flex-col">
        <TabsList variant="line" className="h-8 w-full justify-start bg-transparent p-0">
          <TabsTrigger value="view" className="h-7 flex-none px-2 text-body">
            보기
          </TabsTrigger>
          {canEditSchedule && (
            <TabsTrigger value="schedule" className="h-7 flex-none px-2 text-body">
              일정
            </TabsTrigger>
          )}
          {canEditSchedule && (
            <TabsTrigger value="calendar" className="h-7 flex-none px-2 text-body">
              캘린더
            </TabsTrigger>
          )}
          {isDesktop() && (
            <TabsTrigger value="integration" className="h-7 flex-none px-2 text-body">
              연동
            </TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="view" className={styles.panel}>
          <ViewSettings />
        </TabsContent>
        {canEditSchedule && (
          <TabsContent value="schedule" className={styles.panel}>
            <ScheduleSettings />
          </TabsContent>
        )}
        {canEditSchedule && (
          <TabsContent value="calendar" className={styles.panel}>
            <CalendarSettings />
          </TabsContent>
        )}
        {isDesktop() && (
          <TabsContent value="integration" className={styles.panel}>
            <IntegrationSettings onOpen={(path) => {
              close();
              useSettingsHub.getState().open("app", path.includes("mcp") ? "integration" : "logs");
            }} />
          </TabsContent>
        )}
      </Tabs>
    </AppDialog>
  );
}

function Row({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  const styles = useStyles();
  return (
    <div className={styles.row}>
      <div className={styles.rowText}>
        <div className={styles.rowLabel}>{label}</div>
        {hint && <Caption1 className={styles.hint}>{hint}</Caption1>}
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  const styles = useStyles();
  return (
    <section className={styles.group}>
      <Caption1 className={styles.groupTitle}>{title}</Caption1>
      <div className={styles.groupBody}>{children}</div>
    </section>
  );
}

function ThemeFace({ tone }: { tone: "light" | "dark" }) {
  const styles = useStyles();
  const dark = tone === "dark";
  return (
    <span className={dark ? styles.previewDark : styles.previewLight}>
      <span className={mergeClasses(styles.chrome, dark && styles.chromeDark)} />
      <span className={styles.previewBody}>
        <span className={mergeClasses(styles.rail, dark && styles.railDark)} />
        <span className={styles.lines}>
          <span className={mergeClasses(styles.previewLine, dark && styles.previewLineDark)} />
          <span className={mergeClasses(styles.previewLine, styles.previewLineShort, dark && styles.previewLineDark)} />
        </span>
      </span>
    </span>
  );
}

function ThemePreview({ mode }: { mode: "light" | "dark" | "system" }) {
  if (mode === "light") return <ThemeFace tone="light" />;
  if (mode === "dark") return <ThemeFace tone="dark" />;
  return (
    <>
      <ThemeFace tone="light" />
      <ThemeFace tone="dark" />
    </>
  );
}

const THEME_CHOICES = [
  { value: "light" as const, label: "라이트", desc: "밝고 선명한 기본 테마" },
  { value: "dark" as const, label: "다크", desc: "눈의 피로를 덜어주는 어두운 테마" },
  { value: "system" as const, label: "시스템", desc: "OS 설정에 맞춰 자동 전환" },
];

const DENSITY_CHOICES: Array<{ value: Density; label: string; desc: string; gap: number }> = [
  { value: "compact", label: "컴팩트", desc: "한 화면에 많은 작업 표시", gap: 5 },
  { value: "default", label: "기본", desc: "균형 잡힌 표준 여백과 행 높이", gap: 9 },
  { value: "comfortable", label: "여유", desc: "넉넉하고 편안한 읽기 높이", gap: 14 },
];

/** 데스크탑 전용 「연동」: MCP 연결 상태와 입구, 데이터 위치 안내(T-2.10). */
export function IntegrationSettings({ onOpen }: { onOpen: (path: string) => void }) {
  const status = useConnectionStatus();
  return (
    <>
      <Group title="MCP">
        <Row label="외부 AI 클라이언트" hint={`Cursor·Claude Desktop이 이 앱의 데이터를 읽기 전용으로 봅니다 · ${connectionText(status)}`}>
          <FuiButton density="dialog" appearance="primary" onClick={() => onOpen("/system/mcp")}>
            MCP 연결 열기
          </FuiButton>
        </Row>
        <Row label="로그" hint="도구 호출과 앱 사건을 실시간으로 봅니다">
          <FuiButton density="dialog" appearance="secondary" onClick={() => onOpen("/system/logs")}>
            로그 보기
          </FuiButton>
        </Row>
      </Group>
      <Group title="데이터">
        <Row label="저장 위치" hint="데이터는 이 기기의 앱 저장소에 있습니다. 개발용 실행과 설치한 앱은 저장소가 서로 달라 데이터가 보이지 않을 수 있어요. 옮길 때는 플랜의 JSON 내보내기·가져오기를 쓰세요.">
          <span />
        </Row>
      </Group>
    </>
  );
}

export function AppearanceSettings() {
  const styles = useStyles();
  const ui = useProjectStore((s) => s.ui);
  const setUI = useProjectStore((s) => s.setUI);

  return (
    <div className={styles.appearanceContainer}>
      {/* 1. 테마 설정 카드 */}
      <div className={styles.appearanceCard}>
        <div className={styles.appearanceCardHead}>
          <div className={styles.appearanceCardTitle}>테마 설정</div>
          <Caption1 className={styles.appearanceCardSubtitle}>
            앱의 전반적인 밝기와 인터페이스 색상 모드를 선택합니다.
          </Caption1>
        </div>

        <div
          className={styles.themeCardsGrid}
          role="radiogroup"
          aria-label="테마"
          onKeyDown={(event) => {
            if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
            event.preventDefault();
            const index = THEME_CHOICES.findIndex((choice) => choice.value === ui.theme);
            const step = event.key === "ArrowRight" ? 1 : THEME_CHOICES.length - 1;
            const next = THEME_CHOICES[(index + step) % THEME_CHOICES.length];
            if (next) setUI({ theme: next.value });
          }}
        >
          {THEME_CHOICES.map((choice) => {
            const on = ui.theme === choice.value;
            return (
              <button
                key={choice.value}
                type="button"
                role="radio"
                aria-checked={on}
                className={mergeClasses(styles.themeChoiceCard, on && styles.themeChoiceCardOn)}
                onClick={() => setUI({ theme: choice.value })}
              >
                <div className={styles.themeChoicePreview}>
                  <ThemePreview mode={choice.value} />
                </div>
                <div className={styles.themeChoiceMeta}>
                  <div className={styles.themeChoiceHeader}>
                    <span className={styles.themeChoiceTitle}>{choice.label}</span>
                    {on ? (
                      <CheckmarkCircle16Filled className={styles.themeRadioIcon} />
                    ) : (
                      <Circle16Regular className={styles.themeRadioOff} />
                    )}
                  </div>
                  <span className={styles.themeChoiceDesc}>{choice.desc}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* 2. 화면 표시 밀도 카드 */}
      <div className={styles.appearanceCard}>
        <div className={styles.appearanceCardHead}>
          <div className={styles.appearanceCardTitle}>화면 표시 밀도</div>
          <Caption1 className={styles.appearanceCardSubtitle}>
            작업 표와 간트 차트의 행 높이 및 여백 간격을 조절합니다.
          </Caption1>
        </div>

        <div
          className={styles.densityCardsGrid}
          role="radiogroup"
          aria-label="화면 표시 밀도"
        >
          {DENSITY_CHOICES.map((choice) => {
            const on = ui.density === choice.value;
            return (
              <button
                key={choice.value}
                type="button"
                role="radio"
                aria-checked={on}
                className={mergeClasses(styles.densityCard, on && styles.densityCardOn)}
                onClick={() => setUI({ density: choice.value })}
              >
                <div className={styles.densityLinesWrap} style={{ gap: choice.gap }}>
                  <div className={styles.densityLine} style={{ width: "90%" }} />
                  <div className={styles.densityLine} style={{ width: "70%" }} />
                  <div className={styles.densityLine} style={{ width: "80%" }} />
                </div>
                <div className={styles.themeChoiceMeta}>
                  <div className={styles.themeChoiceHeader}>
                    <span className={styles.themeChoiceTitle}>{choice.label}</span>
                    {on ? (
                      <CheckmarkCircle16Filled className={styles.themeRadioIcon} />
                    ) : (
                      <Circle16Regular className={styles.themeRadioOff} />
                    )}
                  </div>
                  <span className={styles.themeChoiceDesc}>{choice.desc}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* 3. 디자인 시스템 안내 카드 */}
      <div className={styles.appearanceCard}>
        <div className={styles.appearanceCardHead}>
          <div className={styles.appearanceCardTitle}>디자인 시스템 안내</div>
          <Caption1 className={styles.appearanceCardSubtitle}>
            WaveX의 시각 디자인 원칙 및 주요 기능별 색상 가이드입니다.
          </Caption1>
        </div>

        <div className={styles.colorGuideGrid}>
          <div className={styles.colorGuideItem}>
            <span className={styles.colorGuideDot} style={{ backgroundColor: "#0A7AFF" }} />
            <div className={styles.colorGuideText}>
              <span className={styles.colorGuideName}>브랜드 액센트 (#0A7AFF)</span>
              <span className={styles.colorGuideDesc}>행동, 선택 상태 및 핵심 인터랙션에 적용됩니다.</span>
            </div>
          </div>
          <div className={styles.colorGuideItem}>
            <span className={styles.colorGuideDot} style={{ backgroundColor: "#8E8E93" }} />
            <div className={styles.colorGuideText}>
              <span className={styles.colorGuideName}>차분한 표면 (#F9F8F7 / #1C1C1E)</span>
              <span className={styles.colorGuideDesc}>장식을 줄이고 업무 데이터에 집중할 수 있도록 돕습니다.</span>
            </div>
          </div>
          <div className={styles.colorGuideItem}>
            <span className={styles.colorGuideDot} style={{ backgroundColor: "#FF3B30" }} />
            <div className={styles.colorGuideText}>
              <span className={styles.colorGuideName}>위험 및 지연 (#FF3B30)</span>
              <span className={styles.colorGuideDesc}>여유 없는 크리티컬 패스 및 기한 초과 작업에 한정됩니다.</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ViewSettings() {
  const styles = useStyles();
  const ui = useProjectStore((s) => s.ui);
  const setUI = useProjectStore((s) => s.setUI);
  const toggleCol = (key: string, show: boolean) =>
    setHiddenColumns(show ? ui.hiddenColumns.filter((k) => k !== key) : [...ui.hiddenColumns, key]);

  return (
    <div className={styles.viewContainer}>
      {/* 1. 간트 차트 오버레이 카드 */}
      <div className={styles.viewCard}>
        <div className={styles.viewCardHead}>
          <div className={styles.viewCardTitle}>간트 오버레이 및 표시 항목</div>
          <Caption1 className={styles.viewCardSubtitle}>
            간트 타임라인 위에 오버레이로 그려지는 주요 보조선과 하이라이트 항목을 설정합니다.
          </Caption1>
        </div>

        <div className={styles.viewSettingList}>
          <div className={styles.viewItem}>
            <div className={styles.viewItemText}>
              <div className={styles.viewItemTitle}>여유 없는 작업(주 공정) 강조</div>
              <Caption1 className={styles.viewItemHint}>
                하루만 늦어져도 전체 프로젝트 마감이 지연되는 작업을 빨간색으로 강조합니다.
              </Caption1>
            </div>
            <Switch checked={ui.showCritical} onCheckedChange={(v) => setUI({ showCritical: v })} />
          </div>

          <div className={styles.viewItem}>
            <div className={styles.viewItemText}>
              <div className={styles.viewItemTitle}>작업 사이 의존관계 연결선</div>
              <Caption1 className={styles.viewItemHint}>
                선행 작업과 후행 작업 간의 연결 화살표를 표시합니다.
              </Caption1>
            </div>
            <Switch checked={ui.showDependencies} onCheckedChange={(v) => setUI({ showDependencies: v })} />
          </div>

          <div className={styles.viewItem}>
            <div className={styles.viewItemText}>
              <div className={styles.viewItemTitle}>선택 작업의 주 경로 강조</div>
              <Caption1 className={styles.viewItemHint}>
                선택한 작업의 일정을 결정한 선행 경로만 진하게 표시하고 나머지는 흐리게 처리합니다.
              </Caption1>
            </div>
            <Switch checked={ui.highlightDrivingPath} onCheckedChange={(v) => setUI({ highlightDrivingPath: v })} />
          </div>

          <div className={styles.viewItem}>
            <div className={styles.viewItemText}>
              <div className={styles.viewItemTitle}>기준선(초기 계획) 비교선 표시</div>
              <Caption1 className={styles.viewItemHint}>
                작업 막대 아래 얇은 회색 선으로 초기 계획을 표기하며, 지연된 구간은 빗금으로 표시합니다.
              </Caption1>
            </div>
            <Switch checked={ui.showBaseline} onCheckedChange={(v) => setUI({ showBaseline: v })} />
          </div>

          <div className={styles.viewItem}>
            <div className={styles.viewItemText}>
              <div className={styles.viewItemTitle}>진척선(Progress Line)</div>
              <Caption1 className={styles.viewItemHint}>
                진척 기준일에서 각 작업의 실제 완료 지점을 잇는 꺾은선으로 지연 상태를 한눈에 파악합니다.
              </Caption1>
            </div>
            <Switch checked={ui.showProgressLine} onCheckedChange={(v) => setUI({ showProgressLine: v })} />
          </div>

          <div className={styles.viewItem}>
            <div className={styles.viewItemText}>
              <div className={styles.viewItemTitle}>여유 기간(Total Float) 표시</div>
              <Caption1 className={styles.viewItemHint}>
                전체 일정에 영향 없이 미룰 수 있는 여유 일수를 막대 뒤 가는 선으로 표시합니다.
              </Caption1>
            </div>
            <Switch checked={ui.showFloat} onCheckedChange={(v) => setUI({ showFloat: v })} />
          </div>

          <div className={styles.viewItem}>
            <div className={styles.viewItemText}>
              <div className={styles.viewItemTitle}>막대 내부 진척률 텍스트</div>
              <Caption1 className={styles.viewItemHint}>
                작업 막대 좌측에 완료 퍼센트(%)를 표기합니다.
              </Caption1>
            </div>
            <Switch checked={ui.showProgressLabel} onCheckedChange={(v) => setUI({ showProgressLabel: v })} />
          </div>

          <div className={styles.viewItem}>
            <div className={styles.viewItemText}>
              <div className={styles.viewItemTitle}>단계 요약 잔여 기간</div>
              <Caption1 className={styles.viewItemHint}>
                단계 요약 막대 왼쪽에 진척 기준일부터 남은 근무일을 표시합니다.
              </Caption1>
            </div>
            <Switch checked={ui.showRemainingLabel} onCheckedChange={(v) => setUI({ showRemainingLabel: v })} />
          </div>

          <div className={styles.viewItem}>
            <div className={styles.viewItemText}>
              <div className={styles.viewItemTitle}>막대 옆 라벨 글자</div>
              <Caption1 className={styles.viewItemHint}>
                타임라인 막대 우측에 표시할 정보를 선택합니다.
              </Caption1>
            </div>
            <FuiDropdown
              density="dialog"
              value={ui.barLabel}
              options={BAR_LABEL_OPTIONS}
              onValueChange={(v) => setUI({ barLabel: v as BarLabel })}
              style={{ width: 140, minWidth: 140 }}
            />
          </div>
        </div>
      </div>

      {/* 2. 작업 시트 컬럼 및 틀 고정 카드 */}
      <div className={styles.viewCard}>
        <div className={styles.viewCardHead}>
          <div className={styles.viewCardTitle}>시트 컬럼 및 틀 고정</div>
          <Caption1 className={styles.viewCardSubtitle}>
            스프레드시트 형태의 작업 테이블에 표시할 열과 고정 기준을 설정합니다.
          </Caption1>
        </div>

        <div className={styles.viewSettingList}>
          <div className={styles.viewItem}>
            <div className={styles.viewItemText}>
              <div className={styles.viewItemTitle}>좌측 틀 고정 기준</div>
              <Caption1 className={styles.viewItemHint}>
                가로 스크롤 시 화면 좌측에 항상 고정되어 표시될 열의 경계입니다.
              </Caption1>
            </div>
            <FuiDropdown
              density="dialog"
              value={ui.pinnedColumn}
              options={[
                { value: "name", label: "작업명까지" },
                { value: "wbs", label: "WBS 코드까지" },
                { value: "none", label: "고정 안 함" },
              ]}
              onValueChange={(v) => setUI({ pinnedColumn: v as PinBoundary })}
              style={{ width: 140, minWidth: 140 }}
            />
          </div>
        </div>

        <div>
          <Caption1 className={styles.viewItemHint} style={{ marginBottom: "8px", display: "block" }}>
            표시할 선택 컬럼
          </Caption1>
          <div className={styles.columnsGrid}>
            {OPTIONAL_COLUMNS.map((c) => {
              const active = !ui.hiddenColumns.includes(c.key);
              return (
                <label
                  key={c.key}
                  className={mergeClasses(styles.columnChip, active && styles.columnChipOn)}
                >
                  <Checkbox
                    checked={active}
                    onCheckedChange={(v) => toggleCol(c.key, v === true)}
                  />
                  <span style={{ fontSize: "13px" }}>{c.label}</span>
                </label>
              );
            })}
          </div>
        </div>

        <div className={styles.columnActions}>
          <FuiButton
            appearance="subtle"
            density="dialog"
            onClick={() => setHiddenColumns([...DEFAULT_HIDDEN_COLUMNS])}
          >
            기본 열로 복원
          </FuiButton>
          <FuiButton
            appearance="secondary"
            density="dialog"
            onClick={() => setHiddenColumns([])}
          >
            모든 열 표시
          </FuiButton>
        </div>
      </div>
    </div>
  );
}

export function ScheduleSettings() {
  const project = useProjectStore((s) => s.project);
  const setProject = useProjectStore((s) => s.setProject);
  const [threshold, setThreshold] = useState(String(project.criticalThreshold));
  const commitThreshold = () => {
    const n = Math.max(0, Math.round(Number(threshold) || 0));
    setThreshold(String(n));
    if (n !== project.criticalThreshold) setProject({ criticalThreshold: n });
  };
  const duration = normalizeDefaultDuration(project.defaultDuration);
  const [durationText, setDurationText] = useState(String(duration));
  const commitDuration = () => {
    const raw = Number(durationText.trim());
    const n = durationText.trim() && Number.isFinite(raw) ? normalizeDefaultDuration(raw) : duration;
    setDurationText(String(n));
    if (n !== duration) setProject({ defaultDuration: n });
  };
  const insertStart = resolveInsertStart(project.insertStart);
  return (
    <>
      <Group title="플랜">
        <Row label="플랜 시작일" hint="전체 일정이 시작되는 날">
          <DateField value={project.start} onChange={(v) => v && setProject({ start: v })} className="w-[11.25rem]" aria-label="플랜 시작일" />
        </Row>
        <Row label="진척 기준일" hint="계획과 실제 진척을 어느 시점까지 비교할지 정하는 날짜">
          <DateField value={project.statusDate} onChange={(v) => v && setProject({ statusDate: v })} className="w-[11.25rem]" aria-label="진척 기준일" />
        </Row>
      </Group>
      <Group title="계산">
        <Row label="빨간색으로 표시할 여유 기준 (일)" hint="여유가 이 일수 이하면 빨간색으로 표시합니다. 보통 0">
          <FuiInput
            value={threshold}
            inputMode="numeric"
            onChange={(_, d) => setThreshold(d.value)}
            onBlur={commitThreshold}
            onKeyDown={(e) => e.key === "Enter" && commitThreshold()}
            style={{ width: 72, minWidth: 72, textAlign: "right" }}
          />
        </Row>
      </Group>
      <Group title="새 작업">
        <Row label="기본 기간 (일)" hint="작업·하위 작업을 추가할 때의 근무일 수. 1–999일, 마일스톤은 0일">
          <FuiInput
            value={durationText}
            inputMode="numeric"
            aria-label="새 작업 기본 기간 (일)"
            onChange={(_, d) => setDurationText(d.value)}
            onBlur={commitDuration}
            onKeyDown={(e) => e.key === "Enter" && commitDuration()}
            style={{ width: 72, minWidth: 72, textAlign: "right" }}
          />
        </Row>
        <Row label="시작 위치" hint={INSERT_START_OPTIONS.find((o) => o.value === insertStart)?.hint}>
          <FuiDropdown
            value={insertStart}
            options={INSERT_START_OPTIONS.map(({ value, label }) => ({ value, label }))}
            onValueChange={(v) => setProject({ insertStart: v as InsertStart })}
            style={{ width: 150, minWidth: 150 }}
          />
        </Row>
        <Row label="첫 하위 작업이 상위를 이어받기" hint="하위가 없던 작업에 처음 추가하면 기간·진척·담당을 넘깁니다. 끄면 상위 기간이 첫 하위 작업 기간으로 줄어듭니다">
          <Switch checked={project.childInheritsParent ?? true} onCheckedChange={(v) => setProject({ childInheritsParent: v })} />
        </Row>
        <Row label="진척 계산 방식" hint={PROGRESS_METHODS.find((m) => m.v === (project.defaultProgressMethod ?? "percent"))?.hint}>
          <FuiDropdown
            value={project.defaultProgressMethod ?? "percent"}
            options={PROGRESS_METHODS.map((m) => ({ value: m.v, label: m.label }))}
            onValueChange={(v) => setProject({ defaultProgressMethod: v as ProgressMethod })}
            style={{ width: 150, minWidth: 150 }}
          />
        </Row>
      </Group>
    </>
  );
}

type HolidayFilter = "all" | "catalog" | "user";
type PendingHoliday = { kind: "disable" | "enable" | "delete"; row: HolidayRow } | { kind: "year"; year: number };

export function CalendarSettings() {
  const styles = useStyles();
  const stored = useProjectStore((s) => s.project.calendar);
  const def = resolveCalendar(stored);
  const setCalendar = useProjectStore((s) => s.setCalendar);
  const scheduleStart = useProjectStore((s) => s.derived.schedule.projectStart);
  const scheduleFinish = useProjectStore((s) => s.derived.schedule.projectFinish);
  const layer = useDialogLayer();
  const [newHoliday, setNewHoliday] = useState<string | undefined>(undefined);
  const [newName, setNewName] = useState("");
  const [newNote, setNewNote] = useState("");
  const [pickerKey, setPickerKey] = useState(0);
  const [duplicate, setDuplicate] = useState(false);
  const [weekError, setWeekError] = useState(false);
  const [yearOpen, setYearOpen] = useState<Record<number, boolean>>({});
  const [filter, setFilter] = useState<HolidayFilter>("all");
  const [editing, setEditing] = useState<{ id: string; name: string; note: string } | null>(null);
  const [pending, setPending] = useState<PendingHoliday | null>(null);
  const [impact, setImpact] = useState<{ calendar: CalendarDefinition; impact: ScheduleImpact } | null>(null);
  const menuProps = { mountNode: layer, positioning: { strategy: "fixed" as const, position: "below" as const, align: "end" as const } };

  const update = (next: CalendarDefinition) => {
    setCalendar(next);
    const st = useProjectStore.getState();
    if (st.lastImpact) setImpact({ calendar: st.project.calendar, impact: st.lastImpact });
  };
  const pickHoliday = (iso: string | undefined) => {
    setNewHoliday(iso);
    setDuplicate(!!iso && !!holidayDateConflict(def, iso));
  };
  const commitHoliday = () => {
    const name = newName.trim();
    if (!newHoliday || !name || holidayDateConflict(def, newHoliday)) {
      setDuplicate(!!newHoliday && !!holidayDateConflict(def, newHoliday));
      return;
    }
    update(addPlanHoliday(def, { date: newHoliday, name, note: newNote }));
    const year = Number(newHoliday.slice(0, 4));
    setYearOpen((open) => ({ ...open, [year]: true }));
    setNewHoliday(undefined);
    setNewName("");
    setNewNote("");
    setDuplicate(false);
    setPickerKey((key) => key + 1);
    setFilter("all");
  };
  const toggleDay = (day: number) => {
    const next = toggleWorkday(def.workingDays, day);
    setWeekError(!next);
    if (next) update({ ...def, workingDays: next });
  };
  const applyPending = () => {
    if (!pending) return;
    if (pending.kind === "year") update(clearHolidayYear(def, pending.year));
    else if (pending.kind === "delete" && pending.row.userId) update(removePlanHoliday(def, pending.row.userId));
    else if (pending.kind === "disable" || pending.kind === "enable") update(setHolidayRowEnabled(def, pending.row, pending.kind === "enable"));
    setPending(null);
  };

  const selectedCatalogs = new Set(def.holidayCatalogs ?? []);
  const allYears = groupHolidaysByYear(def);
  const years = allYears.map((year) => ({ ...year, rows: year.rows.filter((row) => filter === "all" || row.origin === filter) })).filter((year) => year.rows.length > 0);
  const spanned = yearsBetween(scheduleStart, scheduleFinish);
  const presetId = matchWorkweekPreset(def.workingDays);
  const canAdd = !!newHoliday && !!newName.trim() && !duplicate;

  return (
    <>
      {impact && impact.calendar === def && <ImpactLine impact={impact.impact} />}
      <Group title="근무 요일">
        <div className={styles.weekRow}>
          <div className={styles.dayChips} role="group" aria-label={`근무 요일, ${describeWorkweek(def.workingDays)}`}>
            {WEEK_ORDER.map((d) => (
              <Fragment key={d}>
                {d === 6 && <span className={styles.dayGap} aria-hidden />}
                <button
                  type="button"
                  aria-pressed={def.workingDays[d]}
                  aria-label={`${WEEKDAY_LONG_LABELS[d]} ${def.workingDays[d] ? "근무" : "휴무"}`}
                  className={mergeClasses(styles.dayChip, def.workingDays[d] && styles.dayChipOn)}
                  onClick={() => toggleDay(d)}
                >
                  {WEEKDAY_LABELS[d]}
                </button>
              </Fragment>
            ))}
          </div>
          <div className={styles.presets} role="group" aria-label="근무 주">
            {WORKWEEK_PRESETS.map((preset) => (
              <button
                key={preset.id}
                type="button"
                aria-pressed={presetId === preset.id}
                className={mergeClasses(styles.preset, presetId === preset.id && styles.presetOn)}
                onClick={() => {
                  setWeekError(false);
                  update({ ...def, workingDays: [...preset.days] as CalendarDefinition["workingDays"] });
                }}
              >
                {preset.label}
              </button>
            ))}
          </div>
        </div>
        {weekError && (
          <Caption1 role="alert" className={styles.error}>
            최소 하루는 근무일이어야 합니다.
          </Caption1>
        )}
      </Group>
      <Group title="공휴일">
        <Caption1 className={styles.hint}>캘린더</Caption1>
        <div className={styles.catalogList}>
          {HOLIDAY_CATALOGS.map((catalog) => {
            const on = selectedCatalogs.has(catalog.id);
            return (
              <label key={catalog.id} className={styles.catalogRow}>
                <Checkbox checked={on} onCheckedChange={(value) => update(toggleHolidayCatalog(def, catalog.id, value === true))} aria-label={catalog.label} />
                <span className={styles.catalogCopy}>
                  <span>{catalog.label}</span>
                  <Caption1 className={styles.hint}>{catalog.description}</Caption1>
                </span>
              </label>
            );
          })}
        </div>
        <div className={styles.holidayAdd}>
          <DateField key={pickerKey} value={newHoliday} onChange={pickHoliday} placeholder="날짜 선택" className={styles.holidayDate} aria-label="공휴일 날짜" />
          <FuiInput value={newName} aria-label="공휴일 이름" placeholder="이름" onChange={(_, data) => setNewName(data.value)} style={{ width: 120, minWidth: 96 }} />
          <FuiButton density="dialog" appearance="primary" disabled={!canAdd} onClick={commitHoliday}>
            공휴일 추가
          </FuiButton>
        </div>
        <FuiInput value={newNote} aria-label="공휴일 설명" placeholder="설명" onChange={(_, data) => setNewNote(data.value)} />
        {duplicate && (
          <Caption1 role="alert" className={styles.error}>
            이미 등록된 날입니다. 사용 안 함이면 다시 사용하세요.
          </Caption1>
        )}
        <div className={styles.holidayFilter}>
          <SegmentedControl
            ariaLabel="공휴일 구분"
            value={filter}
            onChange={setFilter}
            options={[
              { value: "all", label: "전체" },
              { value: "catalog", label: "공식" },
              { value: "user", label: "추가" },
            ]}
          />
        </div>
        {allYears.length === 0 ? (
          <GhostEmptyState compact title="등록된 공휴일이 없어요" description="캘린더를 고르거나 날짜와 이름을 넣어 추가하세요." />
        ) : years.length === 0 ? (
          <Caption1 className={styles.filterEmpty}>이 구분의 공휴일이 없어요.</Caption1>
        ) : (
          years.map((y) => {
            const open = yearOpen[y.year] ?? spanned.has(y.year);
            const listId = `holidays-${y.year}`;
            return (
              <section key={y.year} className={styles.year}>
                <div className={styles.yearHead}>
                  <button type="button" className={styles.yearToggle} aria-expanded={open} aria-controls={listId} onClick={() => setYearOpen({ ...yearOpen, [y.year]: !open })}>
                    {open ? <ChevronDown16Regular className="size-3.5" /> : <ChevronRight16Regular className="size-3.5" />}
                    {fmtHolidayYear(y.year)}
                    {filter === "all" && <span className={styles.holidayMuted}>· 일정에 반영 {y.effective}일</span>}
                  </button>
                  <Menu {...menuProps}>
                    <MenuTrigger disableButtonEnhancement>
                      <button type="button" className={styles.menuBtn} aria-label={`${fmtHolidayYear(y.year)} 공휴일 동작`}>
                        <MoreHorizontal16Regular />
                      </button>
                    </MenuTrigger>
                    <MenuPopover className={styles.popover}>
                      <MenuList>
                        <MenuItem onClick={() => setPending({ kind: "year", year: y.year })}>이 연도 빼기</MenuItem>
                      </MenuList>
                    </MenuPopover>
                  </Menu>
                </div>
                {open && (
                  <ul id={listId} className={styles.holidayList}>
                    {y.rows.map((row) => {
                      const editingRow = editing?.id === row.userId;
                      return (
                        <li key={row.key} className={mergeClasses(styles.holiday, !row.enabled && styles.holidayMuted)} title={rowTitle(row)}>
                          <span className={styles.holidayLabel}>{row.label}</span>
                          <span className={styles.holidayBody}>
                            {editingRow && editing ? (
                              <span className={styles.editStack}>
                                <FuiInput value={editing.name} aria-label="공휴일 이름" onChange={(_, data) => setEditing({ ...editing, name: data.value })} />
                                <FuiInput value={editing.note} aria-label="공휴일 설명" placeholder="설명" onChange={(_, data) => setEditing({ ...editing, note: data.value })} />
                                <span className={styles.editActions}>
                                  <FuiButton
                                    density="dialog"
                                    appearance="primary"
                                    disabled={!editing.name.trim()}
                                    onClick={() => {
                                      update(updatePlanHoliday(def, editing.id, { name: editing.name, note: editing.note }));
                                      setEditing(null);
                                    }}
                                  >
                                    저장
                                  </FuiButton>
                                  <FuiButton density="dialog" appearance="subtle" onClick={() => setEditing(null)}>
                                    취소
                                  </FuiButton>
                                </span>
                              </span>
                            ) : (
                              <>
                                <span className={styles.holidayName}>
                                  {row.name}
                                  <span className={styles.holidayMuted}>{` · ${row.sourceLabel}`}</span>
                                  {row.enabled && row.onRestDay && <span className={styles.holidayMuted}> · 쉬는 요일</span>}
                                  {!row.enabled && <span className={styles.holidayMuted}> · 사용 안 함</span>}
                                </span>
                                {row.note && <Caption1 className={styles.holidayNote}>{row.note}</Caption1>}
                              </>
                            )}
                          </span>
                          <Menu {...menuProps}>
                            <MenuTrigger disableButtonEnhancement>
                              <button type="button" className={styles.menuBtn} aria-label={`${row.name} 공휴일 동작`}>
                                <MoreHorizontal16Regular />
                              </button>
                            </MenuTrigger>
                            <MenuPopover className={styles.popover}>
                              <MenuList>
                                {row.origin === "user" && row.userId && (
                                  <MenuItem onClick={() => setEditing({ id: row.userId!, name: row.name, note: row.note ?? "" })}>수정</MenuItem>
                                )}
                                <MenuItem onClick={() => setPending({ kind: row.enabled ? "disable" : "enable", row })}>{row.enabled ? "사용 안 함" : "다시 사용"}</MenuItem>
                                {row.origin === "user" && <MenuItem onClick={() => setPending({ kind: "delete", row })}>삭제</MenuItem>}
                              </MenuList>
                            </MenuPopover>
                          </Menu>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>
            );
          })
        )}
      </Group>
      <ConfirmDialog
        open={!!pending}
        title={pendingTitle(pending)}
        confirmLabel={pendingLabel(pending)}
        message={pending ? pendingMessage(pending) : null}
        onCancel={() => setPending(null)}
        onConfirm={applyPending}
      />
    </>
  );
}

function rowTitle(row: HolidayRow): string | undefined {
  if (!row.enabled) return "사용하지 않아 일정에 반영되지 않습니다";
  if (row.onRestDay) return "쉬는 요일이라 일정에 영향이 없습니다";
  return undefined;
}

function pendingTitle(pending: PendingHoliday | null): string {
  if (pending?.kind === "year") return "연도 공휴일 빼기";
  if (pending?.kind === "enable") return "공휴일 다시 사용";
  if (pending?.kind === "disable") return "공휴일 사용 안 함";
  return "공휴일 삭제";
}

function pendingLabel(pending: PendingHoliday | null): string {
  if (pending?.kind === "year") return "빼기";
  if (pending?.kind === "enable") return "다시 사용";
  if (pending?.kind === "disable") return "사용 안 함";
  return "삭제";
}

function pendingMessage(pending: PendingHoliday): string {
  if (pending.kind === "year") return "이 연도의 공휴일을 뺄까요? 공식 휴일은 사용 안 함으로 남고, 추가한 휴일은 삭제됩니다.";
  if (pending.kind === "enable") return "이 공휴일을 다시 사용할까요? 작업 날짜가 다시 계산됩니다.";
  if (pending.kind === "disable") return "이 공휴일을 사용하지 않을까요? 목록에는 남고 일정에서는 빠집니다.";
  return "추가한 공휴일을 삭제할까요? 목록에서 빠지고 작업 날짜가 다시 계산됩니다.";
}

function ImpactLine({ impact }: { impact: ScheduleImpact }) {
  const styles = useStyles();
  const n = impact.changedTaskIds.length;
  const finish =
    impact.previousFinish === impact.finish
      ? "종료일은 그대로"
      : `종료 ${formatDate(impact.previousFinish, "numeric")} → ${formatDate(impact.finish, "numeric")} (${fmtDelta(impact.finishDeltaDays)})`;
  return (
    <div className={styles.impact} role="status" aria-live="polite">
      <Caption1>{n === 0 ? "일정은 바뀌지 않았습니다." : `작업 ${n}개의 날짜가 바뀌었습니다 · ${finish}`}</Caption1>
    </div>
  );
}
